/**
 * Safely evaluates a formula against    
 * @param formula - The formula to evaluate
 * @param row - The current row data
 * @param columns - Available columns definitions
 * @param allRows - All rows for aggregation functions (optional)
 * @param rowIndex - Current row index for relative aggregations (optional)
 * @param usePreviousValuesOnly - Whether to use only values from rows before the current one (default: true)
 */
export function evaluateFormula(
  formula: string, 
  row: any, 
  columns?: any[], 
  allRows?: any[], 
  rowIndex?: number,
  usePreviousValuesOnly: boolean = true
): any {
  // Detect and process aggregation functions first
  if (allRows && allRows.length > 0) {
    // Filter rows to only include those before the current row in chronological order
    // if usePreviousValuesOnly is true and we can determine the date
    let rowsToUse = allRows;
    
    if (usePreviousValuesOnly && row) {
      const rowDate = row.date || row.harvestDate || row.createdAt;
      
      if (rowDate) {
        const currentDate = new Date(rowDate);
        rowsToUse = allRows.filter(r => {
          const rDate = r.date || r.harvestDate || r.createdAt;
          return rDate && new Date(rDate) <= currentDate;
        });
      }
    }
    
    // Match aggregation patterns like sum(field), avg(field), count(field)
    const aggregationRegex = /(sum|avg|count|min|max)\(([^)]+)\)(?:\s*(?:where\s+([^)]+)\s*)?)?/gi;
    
    let match;
    let processedFormula = formula;
    
    while ((match = aggregationRegex.exec(formula)) !== null) {
      const [fullMatch, fnName, fieldExpr, whereClause] = match;
      let result = null;
      
      try {
        // Filter rows based on where clause if provided
        let filteredRows = rowsToUse;
        if (whereClause) {
          filteredRows = rowsToUse.filter(r => {
            try {
              const whereResult = evaluateFormula(whereClause, r, columns);
              return Boolean(whereResult);
            } catch (e) {
              return false;
            }
          });
        }
        
        // Process rows based on function name
        switch (fnName.toLowerCase()) {
          case 'sum': {
            // Get values for the field expression from each row, exclude nulls
            const values = filteredRows
              .map(r => evaluateFormula(fieldExpr, r, columns))
              .filter(val => val !== null && val !== undefined && !isNaN(val));
            
            // Calculate sum
            result = values.reduce((sum, val) => sum + (Number(val) || 0), 0);
            break;
          }
          case 'avg': {
            // Get values for the field expression from each row, exclude nulls
            const values = filteredRows
              .map(r => evaluateFormula(fieldExpr, r, columns))
              .filter(val => val !== null && val !== undefined && !isNaN(val));
            
            // Calculate average
            result = values.length > 0 
              ? values.reduce((sum, val) => sum + (Number(val) || 0), 0) / values.length 
              : null;
            break;
          }
          case 'count': {
            // Count non-null values
            result = filteredRows
              .filter(r => {
                const val = evaluateFormula(fieldExpr, r, columns);
                return val !== null && val !== undefined;
              })
              .length;
            break;
          }
          case 'min': {
            // Get minimum value
            const values = filteredRows
              .map(r => evaluateFormula(fieldExpr, r, columns))
              .filter(val => val !== null && val !== undefined && !isNaN(val));
            
            result = values.length > 0 ? Math.min(...values) : null;
            break;
          }
          case 'max': {
            // Get maximum value
            const values = filteredRows
              .map(r => evaluateFormula(fieldExpr, r, columns))
              .filter(val => val !== null && val !== undefined && !isNaN(val));
            
            result = values.length > 0 ? Math.max(...values) : null;
            break;
          }
          default:
            result = null;
        }
        
        // Replace the aggregation function with its calculated value
        processedFormula = processedFormula.replace(fullMatch, String(result));
      } catch (error) {
        console.error(`Error evaluating aggregation function "${fullMatch}":`, error);
        processedFormula = processedFormula.replace(fullMatch, 'null');
      }
    }
    
    // Continue with the processed formula after replacing aggregations
    formula = processedFormula;
  }
  
  // Replace field references with row values
  const preparedFormula = formula.replace(/\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g, (match) => {
    // Check if the match is a JavaScript keyword or operator
    const jsKeywords = ['true', 'false', 'null', 'undefined', 'NaN', 'Infinity', 
                        'Math', 'Number', 'String', 'Date', 'if', 'else', 'for', 
                        'while', 'return', 'function'];
    if (jsKeywords.includes(match)) return match;
    
    // Handle dot notation in the formula (e.g., "report.biomass")
    if (match.includes('.')) return match;
    
    // Use column valueGetter if columns are provided
    if (columns) {
      const column = columns.find(col => col.field === match);
      if (column && typeof column.valueGetter === 'function') {
        // Execute the valueGetter and return the literal value
        const value = column.valueGetter(undefined, row);
        
        // Return the literal value rather than a reference
        if (typeof value === 'string') return `"${value}"`;
        if (value === null || value === undefined) return 'null';
        return value;
      }
    }
    
    // Handle nested properties in the row
    if (row.report && row.report[match] !== undefined) {
      return `row.report.${match}`;
    }
    
    // Handle direct properties on the row
    if (row[match] !== undefined) {
      return `row.${match}`;
    }
    
    return match;
  });
  
  try {
    // Use Function constructor instead of eval for better security
    return new Function('row', 'Math', `try { 
        const result = ${preparedFormula};
        return isFinite(result) ? result : null; 
    } catch(e) {  return null; }`)(row, Math);
  } catch (error) {
    console.error(`Error evaluating formula "${formula}":`, error);
    return null;
  }
}
